import {
	getFailureDefinitions,
	getFailureDefinitionsResolved,
	getFailureDefinitionsRejected,
	getDeteriorationModels,
	getDeteriorationModelsResolved,
	getDeteriorationModelsRejected,
} from '@Actions/pipeDeterioration.actions';
import { error } from '@innovyze/stylovyze';
import { put, retry, takeLatest, select } from '@redux-saga/core/effects';
import {
	getFailureDefinitions as getFailureDefinitionsApi,
	getDeteriorationModels as getDeteriorationModelsApi,
} from '@Services/pipeDeterioration/PipeDeterioration.service';
import {
	DeteriorationModel,
	FailureDefinition,
	FailureDefinitionApiResponse,
	GetAllDeteriorationModelsResponse,
	GetAllFailureDefinitionsResponse,
	SingleDeteriorationModelResponse,
	StatusType,
} from '@Types/pipeDeterioration.types';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { RiskConfigSelector } from '@Selectors/Risk.selectors';
import { getSystemTypeIdByAssetTypePrefix } from '@innovyze/lib_asset_schema';
import { RiskConfig } from '@Types/risk.types';

const mapFailureDefinitionResponseToModel = (
	response: FailureDefinitionApiResponse,
): FailureDefinition => ({
	id: response.id,
	name: response.name,
	systemType: response.system_type,
	assetType: response.asset_type,
	inspectionStandard: response.inspection_standard,
	lastRunAt: response.last_run_at,
	status: response.status as StatusType | undefined,
	published: response.published,
});

const mapDeteriorationModelResponseToModel = (
	response: SingleDeteriorationModelResponse,
): DeteriorationModel => ({
	id: response.id,
	name: response.name,
	assetGroupSetId: response.asset_group_set_id,
	failureDefinitionId: response.failure_definition_id,
	status: response.status,
	createdBy: response.created_by,
	createdAt: response.created_at,
	systemType: response.system_type || '',
	assetType: response.asset_type || '',
	runAt: response.run_at,
	latestRunResultsId: response.latest_run_results_id,
});

export function* getFailureDefinitionsSaga(): Generator {
	try {
		const riskConfig = (yield select(RiskConfigSelector)) as RiskConfig;
		const assetType = riskConfig?.riskSetting?.assetType;
		const systemType = assetType
			? getSystemTypeIdByAssetTypePrefix(assetType)
			: undefined;

		const response = (yield retry(
			5,
			1500,
			getFailureDefinitionsApi,
			systemType,
			assetType,
		)) as AxiosResponse<GetAllFailureDefinitionsResponse>;

		const mappedDefinitions: FailureDefinition[] =
			response.data.failure_definitions.map(
				mapFailureDefinitionResponseToModel,
			);

		yield put(
			getFailureDefinitionsResolved({
				failure_definitions: mappedDefinitions,
			}),
		);
	} catch (e) {
		yield put(getFailureDefinitionsRejected());
		yield put(error(t('Failed to get failure definitions')));
	}
}

export function* getDeteriorationModelsSaga(): Generator {
	try {
		const riskConfig = (yield select(RiskConfigSelector)) as RiskConfig;
		const assetType = riskConfig?.riskSetting?.assetType;
		const systemType = assetType
			? getSystemTypeIdByAssetTypePrefix(assetType)
			: undefined;

		const response = (yield retry(
			5,
			1500,
			getDeteriorationModelsApi,
			systemType,
			assetType,
		)) as AxiosResponse<GetAllDeteriorationModelsResponse>;

		const mappedModels: DeteriorationModel[] = response.data.data.map(
			mapDeteriorationModelResponseToModel,
		);

		yield put(getDeteriorationModelsResolved(mappedModels));
	} catch (e) {
		yield put(getDeteriorationModelsRejected());
		yield put(error(t('Failed to get deterioration models')));
	}
}

function* watchPipeDeteriorationSagas() {
	yield takeLatest(getFailureDefinitions, getFailureDefinitionsSaga);
	yield takeLatest(getDeteriorationModels, getDeteriorationModelsSaga);
}

const sagaArray = [watchPipeDeteriorationSagas()];

export default sagaArray;
